import { FC, useCallback } from 'react'
import { Box, Switch, Typography, CircularProgress } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import { useAuth } from '@frontegg/react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { openModal, ModalType } from 'store/modals/modalSlice'
import {
  nexaAIEnabledSelector,
  nexaAIAgentNameSelector,
  nexaAIInProgressSelector,
  nexaAIAgentStateSelector
} from 'store/user/userSlice'
import { AGENT_ACTIVATING_STATES, AgentStateType } from 'models'
import { OnyxAITooltip } from 'ui/pages/cpi-details/sections/trends-tab/sections/OnyxAITooltip'
import { useNexaAIStatus } from 'hooks/useNexaAIStatus'
import { hasRole, Role } from 'lib/utils'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 72px;
`

const Card = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 584px;
  padding: 40px;
  background: ${theme.colors.surfaceContainerBackground};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: 12px;
`

const ContentSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const TitleSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const CardTitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 20px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.menuItemText};
`

const CardDescription = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: ${theme.typography.fontWeight.normal};
  color: ${theme.colors.devicesSecondaryText};
  line-height: 22px;
  letter-spacing: 0.25px;
`

const ToggleRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ToggleLabel = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.menuItemText};
  line-height: 20px;
`

const InfoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: 12px;
`

const InfoTitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: ${theme.typography.fontWeight.extraThick};
  color: ${theme.colors.cta};
  line-height: 20px;
`

const InfoSubtitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 12px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.devicesSecondaryText};
  line-height: 16px;
`

const NexaSettings: FC = () => {
  const dispatch = useAppDispatch()
  const nexaAIEnabled = useAppSelector(nexaAIEnabledSelector)
  const nexaAIAgentName = useAppSelector(nexaAIAgentNameSelector)
  const nexaAIInProgress = useAppSelector(nexaAIInProgressSelector)
  const nexaAIAgentState = useAppSelector(nexaAIAgentStateSelector)
  const { loading, isInitialized } = useNexaAIStatus()
  const { user: authUser } = useAuth()
  const isOwner = hasRole(Role.Owner, authUser)

  const handleToggleClick = useCallback(() => {
    if (!isOwner) return

    dispatch(
      openModal({
        type: ModalType.nexaAIToggle,
        props: {
          nexaAIEnabled,
          nexaAIAgentName: nexaAIAgentName || undefined
        }
      })
    )
  }, [dispatch, nexaAIEnabled, nexaAIAgentName, isOwner])

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing.large }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }
  return (
    <Container>
      <Card>
        <ContentSection>
          <TitleSection>
            <CardTitle>Nexa AI Agent Access</CardTitle>
            <CardDescription>
              Enable or disable Nexa AI agent across your organization. When turned off, AI-based data processing is
              paused.
            </CardDescription>
          </TitleSection>

          <ToggleRow>
            <ToggleLabel>Nexa AI agent</ToggleLabel>
            {isOwner && isInitialized ? (
              nexaAIInProgress ? (
                <OnyxAITooltip
                  title={
                    <>
                      <Typography
                        sx={{
                          fontFamily: theme.typography.fontFamily.primary,
                          fontSize: theme.typography.fontSize.large,
                          fontWeight: theme.typography.fontWeight.bold,
                          color: theme.colors.white
                        }}
                      >
                        {AGENT_ACTIVATING_STATES.includes(nexaAIAgentState as AgentStateType)
                          ? 'Agent is being deployed'
                          : nexaAIAgentState === AgentStateType.DATA_REFRESHING
                          ? 'Agent is refreshing data'
                          : 'Agent is being removed'}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: theme.typography.fontFamily.primary,
                          fontSize: theme.typography.fontSize.medium,
                          fontWeight: theme.typography.fontWeight.normal,
                          color: theme.colors.neutral100
                        }}
                      >
                        {AGENT_ACTIVATING_STATES.includes(nexaAIAgentState as AgentStateType)
                          ? 'The Nexa AI agent is being deployed. This may take a few minutes.'
                          : nexaAIAgentState === AgentStateType.DATA_REFRESHING
                          ? 'The Nexa AI agent is refreshing its data. This may take a few minutes.'
                          : 'The Nexa AI agent is being removed. This may take a moment.'}
                      </Typography>
                    </>
                  }
                  placement="top"
                >
                  <Box sx={{ display: 'inline-flex' }}>
                    <CircularProgress size={20} sx={{ color: theme.colors.cta }} />
                  </Box>
                </OnyxAITooltip>
              ) : (
                <Switch
                  checked={nexaAIEnabled}
                  onClick={handleToggleClick}
                  sx={{
                    width: 38,
                    height: 20,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                      padding: 0,
                      margin: '3px',
                      transitionDuration: '300ms',
                      color: theme.colors.foreground,
                      '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: theme.colors.cta,
                        '& + .MuiSwitch-track': {
                          backgroundColor: theme.colors.surfaceContainerBackground,
                          border: `2px solid ${theme.colors.borderMedium}`,
                          opacity: 1
                        }
                      }
                    },
                    '& .MuiSwitch-thumb': {
                      boxSizing: 'border-box',
                      width: 14,
                      height: 14
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 35,
                      backgroundColor: theme.colors.surfaceContainerBackground,
                      border: `2px solid ${theme.colors.borderMedium}`,
                      opacity: 1
                    }
                  }}
                />
              )
            ) : (
              <OnyxAITooltip
                title={
                  <>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily.primary,
                        fontSize: theme.typography.fontSize.large,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: theme.colors.white
                      }}
                    >
                      {isOwner ? 'Nexa AI is not available' : 'Permission Required'}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily.primary,
                        fontSize: theme.typography.fontSize.medium,
                        fontWeight: theme.typography.fontWeight.normal,
                        color: theme.colors.neutral100
                      }}
                    >
                      {isOwner
                        ? 'This feature is not enabled for your account. Please contact our support team.'
                        : 'Only organization owners can enable or disable Nexa AI agent.'}
                    </Typography>
                  </>
                }
                placement="top"
              >
                <Box sx={{ display: 'inline-flex' }}>
                  <Switch
                    checked={nexaAIEnabled}
                    disabled
                    sx={{
                      width: 38,
                      height: 20,
                      padding: 0,
                      '& .MuiSwitch-switchBase': {
                        padding: 0,
                        margin: '3px',
                        '&.Mui-checked': {
                          transform: 'translateX(18px)'
                        },
                        '&.Mui-disabled': {
                          color: theme.baseColors.greyscale[50]
                        },
                        '&.Mui-disabled + .MuiSwitch-track': {
                          opacity: 0.5
                        }
                      },
                      '& .MuiSwitch-thumb': {
                        boxSizing: 'border-box',
                        width: 14,
                        height: 14
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 35,
                        backgroundColor: theme.baseColors.greyscale[40],
                        border: `2px solid ${theme.colors.borderMedium}`,
                        opacity: 0.5
                      }
                    }}
                  />
                </Box>
              </OnyxAITooltip>
            )}
          </ToggleRow>
        </ContentSection>

        <InfoBox>
          <InfoTitle>More configuration options are coming soon!</InfoTitle>
          <InfoSubtitle>We'll notify you as soon as they're ready.</InfoSubtitle>
        </InfoBox>
      </Card>
    </Container>
  )
}

export default NexaSettings
